import {
  BillingPanel,
  PacingPanel,
  SmoothingSummaryPanel,
  useAnalyticsData,
} from "./AnalyticsPanels";
import TabModal, { TabDescriptor } from "./TabModal";
import WorkRhythmPanel from "./WorkRhythmPanel";
import WorkCadencePanel from "./WorkCadencePanel";
import { Card } from "@lazar/ui";

interface AnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AnalyticsModal = ({ isOpen, onClose }: AnalyticsModalProps) => {
  // Mount the data + panels only while open via the inner body. Keeps the
  // analytics memos from running when the modal is closed, and gives the body's
  // `now` capture (and the default-tab/focus reset in TabModal) a fresh start on
  // every open.
  if (!isOpen) return null;
  return <AnalyticsModalBody onClose={onClose} />;
};

const AnalyticsModalBody = ({ onClose }: { onClose: () => void }) => {
  const {
    hasEntries,
    dayHours,
    workdaysPerWeek,
    getClient,
    now,
    pacing,
    billing,
    smoothingSummary,
  } = useAnalyticsData();

  // Tab order top→bottom is read order for a planning session: pace this week,
  // then reconcile billing. Smoothing is a conditional "impact" lens set off
  // below a divider — it only appears when smoothing is enabled, and Pacing
  // stays the default so toggling smoothing never moves the landing pane.
  const tabs: TabDescriptor[] = [
    {
      id: "pacing",
      label: "This week",
      render: () => (
        <PacingPanel
          rows={pacing.rows}
          paceFrac={pacing.paceFrac}
          dayHours={dayHours}
          workdaysPerWeek={workdaysPerWeek}
          now={now}
        />
      ),
    },
    {
      id: "billing",
      label: "Billing",
      render: () => (
        <BillingPanel
          rows={billing.rows}
          totals={billing.totals}
          dayHours={dayHours}
        />
      ),
    },
    {
      id: "rhythm",
      label: "Rhythm",
      render: () => <WorkRhythmPanel />,
    },
    {
      id: "cadence",
      label: "Work weeks",
      render: () => <WorkCadencePanel />,
    },
  ];

  if (smoothingSummary) {
    tabs.push({
      id: "smoothing",
      label: "Smoothing",
      dividerBefore: true,
      render: () => (
        <SmoothingSummaryPanel
          summary={smoothingSummary}
          getClient={getClient}
          dayHours={dayHours}
        />
      ),
    });
  }

  return (
    <TabModal
      isOpen
      onClose={onClose}
      title="Detailed analytics"
      idPrefix="analytics"
      tablistLabel="Analytics sections"
      closeLabel="Close detailed analytics"
      paneClassName="bg-lemon-50 dark:bg-ink"
      tabs={hasEntries ? tabs : []}
      emptyState={
        hasEntries ? undefined : (
          <Card className="p-6">
            <p className="text-ink-soft dark:text-cream/70">
              Start tracking time to see analytics.
            </p>
          </Card>
        )
      }
    />
  );
};

export default AnalyticsModal;
